import { homeSourcePolicies } from "../pages/Dashboard/workSources";
import { getAvailableTools } from "../features/tools/registry";
import type { ToolId } from "../features/toolExperience/types";
import { useQueries } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { useOrganization } from "../contexts/OrganizationContext";
import {
  getBriefingProjects,
  getBriefingAnalyses,
  type BriefingProject,
  type BriefingAnalysis,
} from "../api/briefing";
import {
  getProjects as getMonitoringProjects,
  getUnacknowledgedChanges,
  type MonitoringProject,
} from "../api/monitoring";
import {
  getProjects as getSimilarityProjects,
  getJobs as getSimilarityJobs,
} from "../api/similarity";
import { listProjects, listBatches } from "../api/clustering";
import { getSnapshots, getJobs as getWordCloudJobs } from "../api/wordcloud";
import { getOAuthStatus } from "../api/analytics";
import { dispatchTokenExpiredEvent } from "../utils/authEvents";
import {
  sortWorkFeed,
  validTimestamp,
  type WorkFeedItem,
} from "../pages/Dashboard/workFeed";
import type { HomeSourceState } from "../pages/Dashboard/homeState";

interface SourceData {
  items: WorkFeedItem[];
  hasWork: boolean;
  count?: number;
  briefingProjects?: BriefingProject[];
  monitoringProjects?: MonitoringProject[];
  completedBriefs?: BriefingAnalysis[];
}
export function useDashboardData() {
  const { user } = useAuth(),
    { currentOrganization } = useOrganization();
  const scope = [user?.id, currentOrganization?.id];
  const sources: Array<{
    id: string;
    toolId: ToolId;
    label: string;
    load: () => Promise<SourceData>;
  }> = [
    {
      id: "briefing-projects",
      toolId: "briefings",
      label: "Briefing projects",
      load: async () => {
        const projects = await getBriefingProjects(
          user,
          dispatchTokenExpiredEvent,
        );
        return {
          hasWork: projects.length > 0,
          count: projects.length,
          briefingProjects: projects,
          items: projects.map((p) => ({
            key: `briefings:project:${p.id}`,
            toolId: "briefings",
            projectId: p.id,
            title: p.name,
            href: `/briefings/${encodeURIComponent(p.id)}`,
            timestamp: validTimestamp(p.updated_at || p.created_at),
            timestampKind: p.updated_at ? "updated" : "created",
            stateLabel: "Project",
          })),
        };
      },
    },
    {
      id: "briefing-runs",
      toolId: "briefings",
      label: "Recent briefs",
      load: async () => {
        const briefs = (
          await getBriefingAnalyses(
            undefined,
            undefined,
            50,
            user,
            dispatchTokenExpiredEvent,
          )
        ).filter(
          (a) =>
            a.analysis_type === "combined" || a.analysis_type === "brief_v2",
        );
        return {
          hasWork: briefs.length > 0,
          completedBriefs: briefs.filter((a) => a.status === "completed"),
          items: briefs.map((a) => ({
            key: `briefings:run:${a.id}`,
            toolId: "briefings",
            projectId: a.project_id,
            title: a.input_data?.keyword || "Content brief",
            href: `/briefings/${encodeURIComponent(a.project_id)}?analysis=${encodeURIComponent(a.id)}`,
            timestamp: validTimestamp(
              a.completed_at || a.updated_at || a.created_at,
            ),
            timestampKind: a.completed_at
              ? "completed"
              : a.updated_at
                ? "updated"
                : "created",
            stateLabel: a.status === "completed" ? "Completed brief" : a.status,
            attentionReason: a.status === "failed" ? "failed" : undefined,
          })),
        };
      },
    },
    {
      id: "monitoring-projects",
      toolId: "monitoring",
      label: "Monitoring websites",
      load: async () => {
        const projects = await getMonitoringProjects();
        return {
          hasWork: projects.length > 0,
          count: projects.length,
          monitoringProjects: projects,
          items: projects.map((p) => ({
            key: `monitoring:project:${p.id}`,
            toolId: "monitoring",
            projectId: p.id,
            title: p.name,
            href: `/monitoring/${encodeURIComponent(p.id)}`,
            timestamp: validTimestamp(p.lastCheck || p.createdAt),
            timestampKind: p.lastCheck ? "checked" : "created",
            stateLabel: p.status,
            attentionReason: p.status === "error" ? "failed" : undefined,
          })),
        };
      },
    },
    {
      id: "similarity-projects",
      toolId: "similarity",
      label: "Similarity projects",
      load: async () => {
        const projects = await getSimilarityProjects(
          user,
          dispatchTokenExpiredEvent,
        );
        return {
          hasWork: projects.length > 0,
          count: projects.length,
          items: projects.map((p) => ({
            key: `similarity:project:${p.id}`,
            toolId: "similarity",
            projectId: p.id,
            title: p.name,
            href: `/similarity/${encodeURIComponent(p.id)}`,
            timestamp: validTimestamp(p.updated_at || p.created_at),
            timestampKind: p.updated_at ? "updated" : "created",
            stateLabel: "Project",
          })),
        };
      },
    },
    {
      id: "similarity-runs",
      toolId: "similarity",
      label: "Recent comparisons",
      load: async () => {
        const jobs = await getSimilarityJobs(
          undefined,
          user,
          dispatchTokenExpiredEvent,
        );
        return {
          hasWork: jobs.length > 0,
          items: jobs.map((j) => ({
            key: `similarity:run:${j.id}`,
            toolId: "similarity",
            projectId: j.project_id,
            title: j.name || j.sitemap_url || "Comparison",
            href: `/similarity/${encodeURIComponent(j.project_id)}/jobs/${encodeURIComponent(j.id)}`,
            timestamp: validTimestamp(
              j.completed_at || j.updated_at || j.created_at,
            ),
            timestampKind: j.completed_at ? "completed" : "created",
            stateLabel: j.status,
            attentionReason: j.status === "failed" ? "failed" : undefined,
          })),
        };
      },
    },
    {
      id: "clustering-projects",
      toolId: "clustering",
      label: "Clustering projects",
      load: async () => {
        const projects = await listProjects();
        return {
          hasWork: projects.length > 0,
          count: projects.length,
          items: projects.map((p) => ({
            key: `clustering:project:${p.id}`,
            toolId: "clustering",
            projectId: p.id,
            title: p.name,
            href: `/clustering/${encodeURIComponent(p.id)}`,
            timestamp: validTimestamp(p.updated_at || p.created_at),
            timestampKind: p.updated_at ? "updated" : "created",
            stateLabel: "Project",
          })),
        };
      },
    },
    {
      id: "clustering-runs",
      toolId: "clustering",
      label: "Recent keyword batches",
      load: async () => {
        const batches = await listBatches({ limit: 5 });
        return {
          hasWork: batches.length > 0,
          items: batches.map((b) => ({
            key: `clustering:run:${b.id}`,
            toolId: "clustering",
            projectId: b.project_id,
            title: b.name || "Keyword batch",
            href: `/clustering/${encodeURIComponent(b.project_id)}/batches/${encodeURIComponent(b.id)}`,
            timestamp: validTimestamp(
              b.completed_at || b.updated_at || b.created_at,
            ),
            timestampKind: b.completed_at ? "completed" : "created",
            stateLabel: b.status,
            attentionReason: b.status === "failed" ? "failed" : undefined,
          })),
        };
      },
    },
    {
      id: "wordcloud-snapshots",
      toolId: "wordcloud",
      label: "Word Cloud results",
      load: async () => {
        const snapshots = await getSnapshots(dispatchTokenExpiredEvent);
        return {
          hasWork: snapshots.length > 0,
          items: snapshots.map((s) => ({
            key: `wordcloud:snapshot:${s.snapshot_id}`,
            toolId: "wordcloud",
            title: s.target_url,
            href: `/wordcloud?snapshot=${encodeURIComponent(s.snapshot_id)}`,
            timestamp: validTimestamp(s.created_at),
            timestampKind: "created",
            stateLabel: "Snapshot",
          })),
        };
      },
    },
    {
      id: "wordcloud-jobs",
      toolId: "wordcloud",
      label: "Word Cloud crawls",
      load: async () => {
        const jobs = await getWordCloudJobs(
          undefined,
          dispatchTokenExpiredEvent,
        );
        return {
          hasWork: jobs.length > 0,
          items: jobs
            .filter((j) => j.status !== "completed")
            .map((j) => ({
              key: `wordcloud:run:${j.id}`,
              toolId: "wordcloud",
              title: j.target_url,
              href: "/wordcloud",
              timestamp: validTimestamp(j.started_at),
              timestampKind: "created",
              stateLabel: j.status,
              attentionReason: j.status === "failed" ? "failed" : undefined,
            })),
        };
      },
    },
    {
      id: "analytics-connection",
      toolId: "analytics",
      label: "Analytics connection",
      load: async () => {
        const connection = await getOAuthStatus();
        return {
          hasWork: connection.connected === true,
          items: connection.connected
            ? [
                {
                  key: "analytics:connection",
                  toolId: "analytics",
                  title: "Analytics",
                  href: "/analytics",
                  timestamp: validTimestamp(connection.connected_at),
                  timestampKind: "created",
                  stateLabel: "Connected",
                },
              ]
            : [],
        };
      },
    },
  ];
  sources.push({
    id: "monitoring-changes",
    toolId: "monitoring",
    label: "Unacknowledged monitoring changes",
    load: async () => {
      const changes = await getUnacknowledgedChanges(50);
      return {
        hasWork: changes.length > 0,
        items: changes.length
          ? [
              {
                key: "monitoring:unacknowledged",
                toolId: "monitoring",
                title: "Monitoring changes",
                href: "/monitoring?changes=1",
                timestamp: null,
                timestampKind: "checked",
                stateLabel: `${changes.length === 50 ? "Latest 50" : changes.length} unacknowledged ${changes.length === 1 ? "change" : "changes"}`,
                attentionReason: "unacknowledged-change",
              },
            ]
          : [],
      };
    },
  });
  const queries = useQueries({
    queries: sources.map((source) => ({
      queryKey: ["dashboard", "work-source", source.id, ...scope],
      queryFn: source.load,
      enabled:
        !!user &&
        !!currentOrganization &&
        homeSourcePolicies[source.toolId] !== "unavailable",
      staleTime: 30_000,
      refetchInterval:
        source.id.startsWith("monitoring-") || source.id.endsWith("-runs")
          ? 30_000
          : (false as const),
      retry: false,
    })),
  });
  const missing = getAvailableTools(user?.role).filter(
    (t) =>
      homeSourcePolicies[t.id] !== "unavailable" &&
      !sources.some((s) => s.toolId === t.id),
  );
  // Keep the partial-failure state visible until its retry has actually succeeded.
  const failed = queries.map(
    (q) => q.isError || (q.isFetching && q.errorUpdatedAt > q.dataUpdatedAt),
  );
  const states: HomeSourceState[] = queries.map((q, i) => ({
    state: failed[i]
      ? q.data
        ? "stale"
        : "error"
      : q.isPending
        ? "loading"
        : "success",
    hasWork: q.data?.hasWork || false,
  }));
  const workFeed = sortWorkFeed(queries.flatMap((q) => q.data?.items || []));
  const counts = sources
    .filter((s) => s.id.endsWith("-projects"))
    .map((s) => ({
      label: s.label,
      value: queries[sources.indexOf(s)].isError
        ? null
        : (queries[sources.indexOf(s)].data?.count ?? null),
    }));
  return {
    sources: [
      ...states,
      ...missing.map(() => ({ state: "error" as const, hasWork: false })),
    ],
    workFeed,
    counts,
    isLoading: queries.some((q) => q.isPending),
    errors: [
      ...sources.filter((_, i) => failed[i]).map((s) => s.label),
      ...missing.map((t) => t.label),
    ],
    retrySources: () =>
      Promise.all(queries.filter((q) => q.isError).map((q) => q.refetch())),
    briefingProjects: queries[0].data?.briefingProjects || [],
    monitoringProjects: queries[2].data?.monitoringProjects || [],
    completedBriefs: queries[1].data?.completedBriefs || [],
  };
}
